import React, { useRef, useState } from "react";
import { Camera, Lock, Save, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { profileApi, apiError, getUser, saveSession, mediaUrl } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function PerfilAdmin() {
  const fileRef = useRef(null);
  const [user, setUser] = useState(getUser());
  const [form, setForm] = useState({ nombre: user?.nombre || "", email: user?.email || "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const persist = (u) => { setUser(u); saveSession({ user: u }); };

  const saveInfo = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre no puede quedar vacío."); return; }
    if (!form.email.trim()) { toast.error("El correo no puede quedar vacío."); return; }
    setSavingInfo(true);
    try {
      const updated = await profileApi.updateAdminProfile({ nombre: form.nombre.trim(), email: form.email.trim() });
      persist(updated);
      toast.success("Perfil actualizado.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingInfo(false); }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("La imagen supera 5 MB."); return; }
    setUploading(true);
    try {
      const { avatar_url } = await profileApi.uploadAvatar(file);
      persist({ ...(getUser() || {}), avatar_url });
      toast.success("Foto actualizada.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setSavingPw(true);
    try {
      await profileApi.changePassword(pw.current_password, pw.new_password);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Contraseña actualizada.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPw(false); }
  };

  const avatar = mediaUrl(user?.avatar_url);
  const initials = (user?.nombre || "A").charAt(0).toUpperCase();

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-profile-page">
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-xl bg-[#0A1C30] flex items-center justify-center">
          <UserCog className="w-7 h-7 text-gold" />
        </span>
        <div>
          <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">ADMINISTRACIÓN</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0A1C30]">Mi perfil</h1>
        </div>
      </div>

      {/* Avatar */}
      <section className="bg-white rounded-3xl ring-1 ring-black/5 p-6 flex items-center gap-5">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-navy flex items-center justify-center ring-4 ring-[var(--gold)]/30">
            {avatar ? <img src={avatar} alt="Avatar" className="w-full h-full object-cover" data-testid="admin-avatar-img" />
              : <span className="text-3xl font-extrabold text-gold">{initials}</span>}
          </div>
          <button data-testid="admin-avatar-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[var(--gold)] text-[#0A1C30] flex items-center justify-center shadow-md hover:scale-105 transition-transform disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickAvatar} data-testid="admin-avatar-input" />
        </div>
        <div>
          <p className="font-extrabold text-[#0A1C30] text-lg">{user?.nombre || "Administrador"}</p>
          <p className="text-sm text-[#6B7688]">{user?.email}</p>
          <p className="text-xs text-[#6B7688] mt-1">JPG, PNG o WEBP · máx 5 MB</p>
        </div>
      </section>

      {/* Usuario */}
      <section className="bg-white rounded-3xl ring-1 ring-black/5 p-6">
        <h2 className="font-extrabold text-[#0A1C30] mb-4">Datos de acceso</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1 block font-bold text-xs">Nombre de usuario</Label>
            <Input data-testid="admin-nombre" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} />
          </div>
          <div>
            <Label className="mb-1 block font-bold text-xs">Correo de acceso</Label>
            <Input data-testid="admin-email" type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
          </div>
        </div>
        <button data-testid="admin-save-info" onClick={saveInfo} disabled={savingInfo}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5 disabled:opacity-60">
          {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios
        </button>
      </section>

      {/* Password */}
      <section className="bg-white rounded-3xl ring-1 ring-black/5 p-6">
        <h2 className="flex items-center gap-2 font-extrabold text-[#0A1C30] mb-4"><Lock className="w-5 h-5 text-[#E9A400]" /> Cambiar contraseña</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="mb-1 block font-bold text-xs">Contraseña actual</Label>
            <Input data-testid="admin-pw-current" type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} />
          </div>
          <div>
            <Label className="mb-1 block font-bold text-xs">Nueva contraseña</Label>
            <Input data-testid="admin-pw-new" type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} />
          </div>
          <div>
            <Label className="mb-1 block font-bold text-xs">Confirmar</Label>
            <Input data-testid="admin-pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} />
          </div>
        </div>
        <button data-testid="admin-pw-save" onClick={changePassword} disabled={savingPw}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-navy text-white font-extrabold px-6 py-2.5 disabled:opacity-60">
          {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Actualizar contraseña
        </button>
      </section>
    </div>
  );
}
